import type { Translations } from "../language";
import { counted } from "../plural";

/**
 * The words every screen shares (the glossary of `src/i18n/README.md`): the units' and builds'
 * names, a count of seconds, hexes and soldiers, and what a command or a build costs; the seats'
 * cards (`render/seats.ts`, `render/seatChooser.ts`, D-203: and the front page's after Play now).
 */
const en = {
  units: { spear: "Spear", sword: "Sword", archer: "Archer", rider: "Rider", siege: "Siege" },
  builds: { tower: "Tower", city: "City", farm: "Farm", hamlet: "Hamlet" },
  seconds: (count: number) => `${count} s`,
  hexes: (count: number) => counted("en", count, { one: "hex", other: "hexes" }),
  soldiers: (count: number) => counted("en", count, { one: "soldier", other: "soldiers" }),
  willCost: (cost: number) => `${cost} Will`,
  supplyCost: (cost: number) => `${cost} supply`,
  versus: (ours: string | number, theirs: string | number) => `${ours} vs ${theirs}`,
  /** L10N3: a room's stand-in name (`net/names.ts`), its three digits, or the bare word with none. */
  guest: (number: string) => (number === "" ? "Guest" : `Guest ${number}`),
  seats: {
    choose: "Choose your role",
    king: "King",
    /** D-203: the King gives the orders; his soldiers march by themselves (the owner's ask). */
    kingLine: "Rule the kingdom. Give orders and your army fights for you.",
    soldier: "Soldier",
    soldierLine: "Fight for the King. Find treasure, sell it, make your name.",
    bank: (coins: number) => `Bank ${coins}`,
  },
};

export const COMMON_TEXT: Translations<typeof en> = {
  en,
  ru: {
    units: {
      spear: "Копейщик",
      sword: "Мечник",
      archer: "Лучник",
      rider: "Всадник",
      siege: "Таран",
    },
    builds: { tower: "Башня", city: "Город", farm: "Ферма", hamlet: "Деревня" },
    seconds: (count) => `${count} с`,
    hexes: (count) =>
      counted("ru", count, { one: "гекс", few: "гекса", many: "гексов", other: "гекса" }),
    soldiers: (count) =>
      counted("ru", count, { one: "солдат", few: "солдата", many: "солдат", other: "солдата" }),
    willCost: (cost) => `воля ${cost}`,
    supplyCost: (cost) => `золото ${cost}`,
    versus: (ours, theirs) => `${ours} на ${theirs}`,
    guest: (number) => (number === "" ? "Гость" : `Гость ${number}`),
    seats: {
      choose: "Выбери роль",
      king: "Король",
      kingLine: "Управляй королевством. Отдавай приказы — армия бьётся за тебя.",
      soldier: "Солдат",
      soldierLine: "Сражайся за Короля. Ищи клады, продавай их и заслужи славу.",
      bank: (coins) => `Кошель ${coins}`,
    },
  },
  uk: {
    units: {
      spear: "Списник",
      sword: "Мечник",
      archer: "Лучник",
      rider: "Вершник",
      siege: "Таран",
    },
    builds: { tower: "Вежа", city: "Місто", farm: "Ферма", hamlet: "Хутір" },
    seconds: (count) => `${count} с`,
    hexes: (count) =>
      counted("uk", count, { one: "гекс", few: "гекси", many: "гексів", other: "гекса" }),
    soldiers: (count) =>
      counted("uk", count, { one: "солдат", few: "солдати", many: "солдатів", other: "солдата" }),
    willCost: (cost) => `воля ${cost}`,
    supplyCost: (cost) => `золото ${cost}`,
    versus: (ours, theirs) => `${ours} на ${theirs}`,
    guest: (number) => (number === "" ? "Гість" : `Гість ${number}`),
    seats: {
      choose: "Обери роль",
      king: "Король",
      kingLine: "Керуй королівством. Віддавай накази — військо б’ється за тебе.",
      soldier: "Солдат",
      soldierLine: "Бийся за Короля. Шукай скарби, продавай їх і здобудь славу.",
      bank: (coins) => `Гаманець ${coins}`,
    },
  },
  es: {
    units: {
      spear: "Lancero",
      sword: "Espadachín",
      archer: "Arquero",
      rider: "Jinete",
      siege: "Ariete",
    },
    builds: { tower: "Torre", city: "Ciudad", farm: "Granja", hamlet: "Aldea" },
    seconds: (count) => `${count} s`,
    hexes: (count) => counted("es", count, { one: "casilla", other: "casillas" }),
    soldiers: (count) => counted("es", count, { one: "soldado", other: "soldados" }),
    willCost: (cost) => `${cost} de voluntad`,
    supplyCost: (cost) => `${cost} de oro`,
    versus: (ours, theirs) => `${ours} contra ${theirs}`,
    guest: (number) => (number === "" ? "Invitado" : `Invitado ${number}`),
    seats: {
      choose: "Elige tu rol",
      king: "Rey",
      kingLine: "Lidera tu reino. Da las órdenes y tu ejército lucha por ti.",
      soldier: "Soldado",
      soldierLine: "Lucha por el Rey. Busca tesoros, véndelos y hazte un nombre.",
      bank: (coins) => `Bolsa ${coins}`,
    },
  },
};
